import React, { useState, useEffect, useRef } from "react";
import { FaDollarSign, FaMobile, FaCheckCircle, FaSearch, FaClock, FaMoneyBillWave, FaInfoCircle, FaSyncAlt } from "react-icons/fa";

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap');

  .pay-wrap * { box-sizing: border-box; }
  .pay-wrap {
    background: #0a0a0a;
    padding: 2rem;
    border-radius: 24px;
  }

  /* ── Info Banner ── */
  .pay-info-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    background: rgba(59,130,246,.06);
    border: 1px solid rgba(59,130,246,.2);
    border-radius: 14px;
    padding: .875rem 1.125rem;
    margin-bottom: 1.25rem;
    font-family: 'DM Sans', sans-serif;
    font-size: .82rem;
    color: #93c5fd;
    line-height: 1.5;
  }
  .pay-info-banner strong { color: #bfdbfe; }
  .pay-info-banner svg { flex-shrink: 0; margin-top: 2px; color: #60a5fa; }

  /* ── Summary Cards ── */
  .pay-summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 1rem;
    margin-bottom: 1.75rem;
  }

  .pay-stat-card {
    background: #111;
    border: 1px solid rgba(255,255,255,.07);
    border-radius: 18px;
    padding: 1.375rem 1.5rem;
    position: relative;
    overflow: hidden;
    cursor: default;
    transition: transform .2s, border-color .2s, box-shadow .2s;
  }
  .pay-stat-card::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at top left, var(--card-glow, rgba(255,215,0,.06)), transparent 65%);
    pointer-events: none;
  }
  .pay-stat-card:hover {
    transform: translateY(-3px);
    border-color: var(--card-accent, rgba(255,215,0,.25));
    box-shadow: 0 12px 32px rgba(0,0,0,.4);
  }
  .pay-stat-card .card-label {
    font-family: 'DM Sans', sans-serif;
    font-size: .68rem;
    font-weight: 700;
    letter-spacing: .1em;
    text-transform: uppercase;
    color: #555;
    margin-bottom: .75rem;
  }
  .pay-stat-card .card-val {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    font-weight: 800;
    line-height: 1;
    color: var(--card-accent, #FFD700);
    margin-bottom: .375rem;
  }
  .pay-stat-card .card-icon {
    position: absolute;
    top: 1.25rem;
    right: 1.25rem;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: var(--card-icon-bg, rgba(255,215,0,.08));
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--card-accent, #FFD700);
  }

  /* ── Search ── */
  .pay-search-bar {
    background: #111;
    border: 1.5px solid rgba(255,255,255,.08);
    border-radius: 14px;
    padding: .875rem 1.25rem;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 1.25rem;
    transition: border-color .2s;
  }
  .pay-search-bar:focus-within {
    border-color: rgba(255,215,0,.35);
    box-shadow: 0 0 0 3px rgba(255,215,0,.06);
  }
  .pay-search-bar input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: #e0e0e0;
    font-family: 'DM Sans', sans-serif;
    font-size: .92rem;
  }
  .pay-search-bar input::placeholder { color: #444; }

  /* ── Table Outer: owns the visual chrome (bg/border/radius) so the
     footer below can sit outside the horizontally-scrolling inner shell ── */
  .pay-table-outer {
    background: #111;
    border: 1px solid rgba(255,255,255,.07);
    border-radius: 18px;
    overflow: hidden;
  }

  .pay-table-shell table {
    width: 100%;
    border-collapse: collapse;
  }

  /* ── Table Head ── */
  .pay-table-shell thead tr {
    background: linear-gradient(90deg, #1a1400, #0d1a0d);
    border-bottom: 1px solid rgba(255,215,0,.12);
  }
  .pay-table-shell thead th {
    padding: 1rem 1.25rem;
    font-family: 'DM Sans', sans-serif;
    font-size: .68rem;
    font-weight: 700;
    letter-spacing: .1em;
    text-transform: uppercase;
    color: #666;
    text-align: left;
    white-space: nowrap;
  }
  .pay-table-shell thead th:first-child { padding-left: 1.75rem; }
  .pay-table-shell thead th:last-child  { padding-right: 1.75rem; text-align: right; }

  /* ── Table Rows ── */
  .pay-table-shell tbody tr {
    border-bottom: 1px solid rgba(255,255,255,.04);
    transition: background .15s;
  }
  .pay-table-shell tbody tr:last-child { border-bottom: none; }
  .pay-table-shell tbody tr:hover { background: rgba(255,215,0,.025); }

  .pay-table-shell tbody td {
    padding: 1.1rem 1.25rem;
    font-family: 'DM Sans', sans-serif;
    font-size: .86rem;
    color: #bbb;
    vertical-align: middle;
  }
  .pay-table-shell tbody td:first-child { padding-left: 1.75rem; }
  .pay-table-shell tbody td:last-child  { padding-right: 1.75rem; text-align: right; }

  /* ── Job ID badge ── */
  .pay-job-id {
    font-family: 'JetBrains Mono', monospace;
    font-size: .78rem;
    font-weight: 600;
    color: #FFD700;
    background: rgba(255,215,0,.08);
    border: 1px solid rgba(255,215,0,.15);
    border-radius: 8px;
    padding: 4px 10px;
    display: inline-block;
  }

  /* ── Client cell ── */
  .pay-client-name {
    font-weight: 600;
    color: #e8e8e8;
  }

  /* ── Service tag ── */
  .pay-service-tag {
    background: rgba(255,255,255,.05);
    border: 1px solid rgba(255,255,255,.08);
    border-radius: 8px;
    padding: 3px 10px;
    font-size: .8rem;
    color: #888;
    display: inline-block;
  }

  /* ── Money cells ── */
  .pay-amount-total {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    color: #e8e8e8;
    font-size: .88rem;
  }
  .pay-amount-cut {
    font-family: 'JetBrains Mono', monospace;
    font-size: .84rem;
    color: #3b82f6;
    font-weight: 500;
  }
  .pay-amount-net {
    font-family: 'JetBrains Mono', monospace;
    font-size: .88rem;
    color: #22c55e;
    font-weight: 700;
  }

  /* ── Phone ── */
  .pay-phone {
    font-family: 'JetBrains Mono', monospace;
    font-size: .8rem;
    color: #555;
  }

  /* ── Status pill ── */
  .pay-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border-radius: 20px;
    padding: 4px 12px;
    font-family: 'DM Sans', sans-serif;
    font-size: .7rem;
    font-weight: 700;
    letter-spacing: .04em;
    white-space: nowrap;
  }
  .pay-status-pill.awaiting-client {
    background: rgba(255,215,0,.1);
    color: #FFD700;
    border: 1px solid rgba(255,215,0,.2);
  }
  .pay-status-pill.processing {
    background: rgba(59,130,246,.1);
    color: #60a5fa;
    border: 1px solid rgba(59,130,246,.2);
  }
  .pay-status-pill.disbursed {
    background: rgba(34,197,94,.1);
    color: #4ade80;
    border: 1px solid rgba(34,197,94,.2);
  }
  .pay-status-pill .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: pulse-dot 2s infinite;
  }
  .pay-status-pill.disbursed .dot { animation: none; }
  @keyframes pulse-dot {
    0%, 100% { opacity: 1; }
    50%       { opacity: .4; }
  }

  /* ── Transaction ref ── */
  .pay-txn-ref {
    font-family: 'JetBrains Mono', monospace;
    font-size: .76rem;
    color: #666;
  }
  .pay-txn-ref.none { color: #333; font-style: italic; }

  /* ── Empty State ── */
  .pay-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5rem 2rem;
    gap: 1rem;
  }
  .pay-empty .icon-ring {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: rgba(34,197,94,.08);
    border: 1px solid rgba(34,197,94,.2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #22c55e;
  }
  .pay-empty h4 {
    font-family: 'Playfair Display', serif;
    font-size: 1.1rem;
    font-weight: 800;
    color: #e0e0e0;
    margin: 0;
  }
  .pay-empty p {
    font-size: .84rem;
    color: #555;
    margin: 0;
  }

  /* ── Table Outer wrapper: hosts the edge-fade scroll hint ── */
  .pay-table-outer {
    position: relative;
  }
  .pay-table-outer::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 40px;
    background: linear-gradient(to right, transparent, #111 85%);
    pointer-events: none;
    border-radius: 0 18px 18px 0;
  }
  .pay-table-outer.at-end::after { opacity: 0; }

  /* ── Responsive ──
     Scroll is always available (not just under a breakpoint) because 9
     columns can exceed the container width well above 900px depending on
     where this dashboard is embedded (sidebar layouts, narrower panes).
     Native OS scrollbars (esp. macOS, touch devices) are often invisible
     until the user actively scrolls, so the ::after edge-fade above gives
     a persistent visual cue that there's more to see. */
  .pay-table-shell {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-color: rgba(255,215,0,.35) #111;
    scrollbar-width: thin;
  }
  .pay-table-shell table {
    min-width: 920px;
  }
  .pay-table-shell::-webkit-scrollbar { height: 10px; }
  .pay-table-shell::-webkit-scrollbar-track { background: #111; }
  .pay-table-shell::-webkit-scrollbar-thumb {
    background: rgba(255,215,0,.3);
    border-radius: 8px;
    border: 2px solid #111;
  }
  .pay-table-shell::-webkit-scrollbar-thumb:hover {
    background: rgba(255,215,0,.5);
  }
`;

const SAMPLE_JOBS = [
  { id: 1042, name: "Grace Wanjiru", service: "Plumbing repair", budget: 8500, craftsman_phone: "+254 712 345 678", payment_status: "disbursed", intasend_ref: "ISK-8834AC" },
  { id: 1041, name: "David Otieno", service: "Electrical wiring", budget: 15200, craftsman_phone: "+254 733 221 990", payment_status: "processing", intasend_ref: "ISK-7712FE" },
  { id: 1039, name: "Amina Hassan", service: "Furniture assembly", budget: 3400, craftsman_phone: "+254 700 556 213", payment_status: "awaiting_client", intasend_ref: null },
  { id: 1037, name: "Peter Kamau", service: "Painting", budget: 12000, craftsman_phone: "+254 721 884 402", payment_status: "disbursed", intasend_ref: "ISK-6650BB" },
  { id: 1035, name: "Faith Nyambura", service: "Carpentry", budget: 6750, craftsman_phone: "+254 745 900 118", payment_status: "processing", intasend_ref: "ISK-5521DD" },
  { id: 1033, name: "Samuel Mwangi", service: "Tiling", budget: 9800, craftsman_phone: "+254 710 662 345", payment_status: "awaiting_client", intasend_ref: null },
];

/**
 * Payment flow is: Client pays via IntaSend → IntaSend disburses to the
 * craftsman automatically → this dashboard is a read-only tracker of that
 * process. Admin no longer manually triggers M-Pesa payouts.
 *
 * Expected job fields (adjust to match your IntaSend webhook payload):
 *   job.payment_status: 'awaiting_client' | 'processing' | 'disbursed'
 *   job.intasend_ref:   transaction/reference id from IntaSend (optional)
 */
export default function AdminPaymentDashboard({ jobsReadyForPayment = SAMPLE_JOBS }) {
  const [search, setSearch] = useState('');
  const [atScrollEnd, setAtScrollEnd] = useState(true);
  const scrollRef = useRef(null);

  const filtered = jobsReadyForPayment.filter(j =>
    (j.name    || '').toLowerCase().includes(search.toLowerCase()) ||
    (j.service || '').toLowerCase().includes(search.toLowerCase())
  );

  const checkScrollEnd = (el) => {
    if (!el) return;
    setAtScrollEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  };

  const handleTableScroll = (e) => checkScrollEnd(e.target);

  useEffect(() => {
    checkScrollEnd(scrollRef.current);
    const onResize = () => checkScrollEnd(scrollRef.current);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [filtered.length]);

  const totalPending = filtered.reduce((a, j) => a + (Number(j.budget) || 0), 0);
  const platformCut  = Math.round(totalPending * 0.1);
  const craftsmanNet = totalPending - platformCut;

  const disbursedCount = filtered.filter(j => j.payment_status === 'disbursed').length;

  const getStatus = (job) => {
    const s = job.payment_status || 'awaiting_client';
    if (s === 'disbursed')  return { cls: 'disbursed',       label: 'Disbursed',              icon: <FaCheckCircle size={11}/> };
    if (s === 'processing') return { cls: 'processing',      label: 'Processing via IntaSend', icon: <FaSyncAlt size={11}/> };
    return                        { cls: 'awaiting-client', label: 'Awaiting Client Payment', icon: null };
  };

  const summaryCards = [
    {
      lbl: 'Jobs in Payout Flow',
      val: filtered.length,
      icon: <FaClock size={18}/>,
      accent: '#FFD700',
      glow: 'rgba(255,215,0,.08)',
      iconBg: 'rgba(255,215,0,.08)',
    },
    {
      lbl: 'Total Value',
      val: `KSh ${totalPending.toLocaleString()}`,
      icon: <FaMoneyBillWave size={18}/>,
      accent: '#22c55e',
      glow: 'rgba(34,197,94,.06)',
      iconBg: 'rgba(34,197,94,.08)',
    },
    {
      lbl: 'Platform Revenue (10%)',
      val: `KSh ${platformCut.toLocaleString()}`,
      icon: <FaDollarSign size={18}/>,
      accent: '#3b82f6',
      glow: 'rgba(59,130,246,.06)',
      iconBg: 'rgba(59,130,246,.08)',
    },
    {
      lbl: 'Net to Craftsmen',
      val: `KSh ${craftsmanNet.toLocaleString()}`,
      icon: <FaMobile size={18}/>,
      accent: '#8b5cf6',
      glow: 'rgba(139,92,246,.06)',
      iconBg: 'rgba(139,92,246,.08)',
    },
  ];

  return (
    <div className="pay-wrap">
      <style>{STYLES}</style>

      {/* ── Info Banner: clarifies admin's role in this flow ── */}
      <div className="pay-info-banner">
        <FaInfoCircle size={14}/>
        <span>
          <strong>Payments are automated via IntaSend.</strong> Clients pay for completed jobs directly through the platform, and IntaSend disburses funds to the craftsman's M-Pesa automatically. This view is read-only — use it to track payout status, not to trigger payments.
        </span>
      </div>

      {/* ── Summary Cards ── */}
      <div className="pay-summary-grid">
        {summaryCards.map((s, i) => (
          <div
            key={i}
            className="pay-stat-card"
            style={{
              '--card-accent':  s.accent,
              '--card-glow':    s.glow,
              '--card-icon-bg': s.iconBg,
            }}
          >
            <div className="card-icon">{s.icon}</div>
            <div className="card-label">{s.lbl}</div>
            <div className="card-val">{s.val}</div>
          </div>
        ))}
      </div>

      {/* ── Search ── */}
      <div className="pay-search-bar">
        <FaSearch size={15} style={{ color:'#444', flexShrink:0 }}/>
        <input
          placeholder="Search by client name or service…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <span
            onClick={() => setSearch('')}
            style={{ color:'#555', cursor:'pointer', fontSize:'.8rem', flexShrink:0 }}
          >
            Clear
          </span>
        )}
      </div>

      {/* ── Table ── */}
      <div className={`pay-table-outer${atScrollEnd ? ' at-end' : ''}`}>
        <div className="pay-table-shell" ref={scrollRef} onScroll={handleTableScroll}>
        {filtered.length === 0 ? (
          <div className="pay-empty">
            <div className="icon-ring"><FaCheckCircle size={26}/></div>
            <h4>Nothing to Track</h4>
            <p>{search ? 'No results match your search.' : 'No jobs are currently in the payout pipeline.'}</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Job #</th>
                <th>Client</th>
                <th>Service</th>
                <th>Total</th>
                <th>Platform 10%</th>
                <th>Net to Craftsman</th>
                <th>Phone</th>
                <th>IntaSend Ref</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(job => {
                const total  = Number(job.budget) || 0;
                const cut    = Math.round(total * 0.1);
                const net    = total - cut;
                const status = getStatus(job);

                return (
                  <tr key={job.id}>
                    <td><span className="pay-job-id">#{job.id}</span></td>
                    <td><span className="pay-client-name">{job.name || '—'}</span></td>
                    <td><span className="pay-service-tag">{job.service || '—'}</span></td>
                    <td><span className="pay-amount-total">KSh {total.toLocaleString()}</span></td>
                    <td><span className="pay-amount-cut">KSh {cut.toLocaleString()}</span></td>
                    <td><span className="pay-amount-net">KSh {net.toLocaleString()}</span></td>
                    <td>
                      <span className="pay-phone">
                        {job.craftsman?.phone || job.craftsman_phone || '—'}
                      </span>
                    </td>
                    <td>
                      {job.intasend_ref
                        ? <span className="pay-txn-ref">{job.intasend_ref}</span>
                        : <span className="pay-txn-ref none">—</span>
                      }
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span className={`pay-status-pill ${status.cls}`}>
                        {status.icon ? status.icon : <span className="dot"/>}
                        {status.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </div>

        {/* Footer count */}
        {filtered.length > 0 && (
          <div style={{
            padding: '.875rem 1.75rem',
            borderTop: '1px solid rgba(255,255,255,.04)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize:'.78rem', color:'#444', fontFamily:'DM Sans,sans-serif' }}>
              Showing {filtered.length} of {jobsReadyForPayment.length} job{jobsReadyForPayment.length !== 1 ? 's' : ''}
            </span>
            <span style={{ fontSize:'.78rem', color:'#333', fontFamily:'JetBrains Mono,monospace' }}>
              {disbursedCount} disbursed · {filtered.length - disbursedCount} in progress
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
